import type { PerformancePart } from '@tunasong/schemas'
import { normalizeParts } from '@tunasong/schemas'
import type { PerformanceConfig } from './performance-config.hook.js'
import type { BandRef, SongBand } from './song-band.hook.js'

/**
 * Where the resolved parts came from. 'song' is this person's override for the song ("This song only").
 * 'session' is a dismissed picker: Full chart for this visit, nothing saved.
 */
export type PartsSource = 'song' | 'band' | 'default' | 'session'

export type PartResolution =
  | { status: 'resolving' }
  | {
      status: 'resolved'
      parts: PerformancePart[]
      source: PartsSource
      band: BandRef | null
      /** The one-time "Playing X, your default part." notice for this band. */
      showDefaultNotice: boolean
    }
  | { status: 'ask'; band: BandRef | null; hasDefault: boolean }

export interface ResolvePartsInput {
  songBand: SongBand
  config: PerformanceConfig
  /** This person's override for the song in view, from its `usersettings` row. */
  songParts?: PerformancePart[]
  /** Parts for this visit only, after the picker was dismissed for this band (or for personal songs). */
  sessionParts?: PerformancePart[]
}

/**
 * Which parts to render a song with: the person's override for the song, else their set for the song's band,
 * else their profile default, else ask. A level is only ever read here, never copied into another.
 */
export const resolveParts = ({ songBand, config, songParts, sessionParts }: ResolvePartsInput): PartResolution => {
  const band = songBand.status === 'band' ? songBand.band : null
  /** The override does not depend on the band, so it applies while the band still resolves */
  if (songParts) {
    return { status: 'resolved', parts: songParts, source: 'song', band, showDefaultNotice: false }
  }
  if (songBand.status === 'resolving') {
    return { status: 'resolving' }
  }
  const bandParts = band ? normalizeParts(config.partsByBand?.[band.id]) : undefined
  if (band && bandParts) {
    return { status: 'resolved', parts: bandParts, source: 'band', band, showDefaultNotice: false }
  }
  const defaultParts = normalizeParts(config.defaultParts)
  if (defaultParts) {
    const showDefaultNotice = band ? !(config.acknowledgedDefaultBands ?? []).includes(band.id) : false
    return { status: 'resolved', parts: defaultParts, source: 'default', band, showDefaultNotice }
  }
  if (sessionParts) {
    return { status: 'resolved', parts: sessionParts, source: 'session', band, showDefaultNotice: false }
  }
  return { status: 'ask', band, hasDefault: false }
}
